export const COACHING_INSIGHTS = {
  business: {
    title: "Business Coaching Tools",
    tools: [] // Tools are now loaded dynamically from backend
  },
  mentormind: {
    title: "Business Growth Frameworks",
    tools: [] // Tools are now loaded dynamically from backend via /api/v1/frameworks
  },
  ecommerce: {
    title: "E-Commerce Coaching Tools",
    tools: [
      { id: 'sku', name: 'SKU Profitability', icon: '📊', description: 'Analyze top/worst SKUs' },
      { id: 'ppc', name: 'PPC Optimizer', icon: '🎯', description: 'Surface wasted ad spend' },
      { id: 'ops', name: 'Ops Efficiency', icon: '📦', description: 'Fulfillment/returns opportunities' }
    ]
  },
  health: {
    title: "Health & Fitness Coaching Tools",
    tools: [
      { id: 'program', name: 'Program Design', icon: '💪', description: 'Build 12-week coaching plan' },
      { id: 'nutrition', name: 'Nutrition Planner', icon: '🥗', description: 'Client-specific guidelines' },
      { id: 'habits', name: 'Habit Builder', icon: '🔄', description: 'Habit loops and accountability' }
    ]
  },
  life: {
    title: "Life Coaching Tools",
    tools: [
      { id: 'clarity', name: 'Clarity Map', icon: '🗺️', description: 'Values → goals → actions' },
      { id: 'keystone', name: 'Keystone Habits', icon: '🔑', description: 'Pick, stack, and track 3 habits' },
      { id: 'balance', name: 'Life Balance', icon: '⚖️', description: 'Assess balance across key areas' }
    ]
  },
  career: {
    title: "Career Coaching Tools",
    tools: [
      { id: 'resume', name: 'Resume Booster', icon: '📄', description: 'Impact bullets with metrics' },
      { id: 'interview', name: 'Interview Coach', icon: '🎤', description: 'Mock Q&A, STAR stories' },
      { id: 'path', name: 'Career Path', icon: '🛤️', description: 'Next role and skill gaps' }
    ]
  },
  relationship: {
    title: "Relationship Coaching Tools",
    tools: [
      { id: 'communication', name: 'Communication Toolkit', icon: '💬', description: 'Nonviolent communication prompts' },
      { id: 'attachment', name: 'Attachment Lens', icon: '🔗', description: 'Patterns and boundaries' },
      { id: 'rituals', name: 'Rituals', icon: '💕', description: 'Weekly connection practices' }
    ]
  },
  financial: {
    title: "Financial Coaching Tools",
    tools: [
      { id: 'spending', name: 'Spending Plan', icon: '💳', description: 'Monthly plan and categories' },
      { id: 'debt', name: 'Debt Strategy', icon: '📉', description: 'Snowball/avalanche plan' },
      { id: 'investing', name: 'Investing 101', icon: '📈', description: 'Index funds and risk profile' }
    ]
  },
  leadership: {
    title: "Leadership Development Tools",
    tools: [
      { id: 'replacement', name: 'Delegation Ladder', icon: '🪜', description: 'Delegate and elevate' },
      { id: 'coaching', name: 'Decision Framework 1-3-1', icon: '👥', description: 'Coach decision-making' },
      { id: 'cadence', name: 'Team Cadence', icon: '📅', description: 'Meetings, KPIs, scorecards' }
    ]
  },
  sales: {
    title: "Sales Coaching Tools",
    tools: [
      { id: 'pipeline', name: 'Pipeline Review', icon: '🔄', description: 'Stages, velocity, next actions' },
      { id: 'script', name: 'Call Script Coach', icon: '📞', description: 'Openers, discovery, closes' },
      { id: 'objections', name: 'Objection Handling', icon: '🛡️', description: 'Patterns and responses' }
    ]
  }
};

// Generic slot extractor across domains
// Returns a normalized object of extracted fields used for routing/tools
export const extractSlots = (text = '', type = 'business') => {
  const t = String(text || '').toLowerCase();
  const slots = {};
  // Finance-like
  const money = t.match(/\$?\s?\d+(?:\.\d+)?\s*[mk]?\s*(mrr|arr|revenue|sales)/i);
  if (money) slots.revenue = money[0];
  const debt = t.match(/\$?\s?\d+(?:,\d+)*(?:\.\d+)?\s*(?:debt)/i);
  if (debt) slots.debt = debt[0];
  const apr = t.match(/(\d{1,2}(?:\.\d+)?)%\s*apr/i);
  if (apr) slots.apr = apr[1];
  const budget = t.match(/\$?\s?\d+(?:,\d+)*(?:\.\d+)?\s*\/??\s*(?:mo|month)/i);
  if (budget) slots.budget = budget[0];
  // Team / hours
  const team = t.match(/\b(\d{1,3})\s*(people|employees|team)\b/i);
  if (team) slots.teamSize = team[1];
  const hours = t.match(/\b(\d{1,3})\s*(h|hrs|hours)\b/i);
  if (hours) slots.hoursPerWeek = hours[1];
  // Health
  const weightNow = t.match(/\b(\d{2,3})(?:\.?\d+)?\s*(lbs?|pounds?|kg)\b/i);
  if (weightNow) slots.currentWeight = weightNow[1];
  const weightGoal = t.match(/(drop|lose|down\s*to|goal)\s*(?:to\s*)?(\d{2,3})\s*(lbs?|pounds?|kg)/i);
  if (weightGoal) slots.goalWeight = weightGoal[2];
  // Sales/process/challenges
  if (/(pipeline|deal|close|objection)/i.test(t)) slots.sales = true;
  if (/(process|workflow|sop|efficiency)/i.test(t)) slots.process = true;
  if (/(struggling|challenge|stuck|blocked|bottleneck)/i.test(t)) slots.challenge = true;
  // Relationship
  if (/(argue|argument|fight|conflict)/i.test(t)) slots.relationshipConflict = true;
  // HVAC safety
  if (/(no-?cool|lockout|compressor|refrigerant)/i.test(t)) slots.hvacIssue = true;
  return slots;
};